/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6e2c-3 — the mini-3D "AHU" preview canvas (§33.8 _attachAhuCanvas /
 * _updateCanvasPreview / resetToDefaultView).
 *
 * REUSE of the 6c2 factory: createAccessoryPreviewCanvas(live, {el, canvas,
 * accessoryId}) clones the live canvas caches and returns a second
 * Canvas3DGeometry. This hook owns its lifecycle inside the selector:
 *  - lazily builds the preview canvas against the AHU-tab host element when the
 *    AHU tab first becomes visible (legacy lazy `_attachAhuCanvas`);
 *  - re-parents renderer.domElement + re-points the el on every (re)attach,
 *    with the requestAnimationFrame resize that fixes the upside-down scene
 *    (legacy :1757-1765);
 *  - writes previewAccessoryStagingData / previewMounting and calls
 *    drawAccessory / destroyAccessory directly (legacy _updateCanvasPreview),
 *    including the removal rules (damper/louver plane 9 / no master / invalid
 *    blade; hood style 4 without height_2);
 *  - wires attachHoverInteraction() (canvas3dInteraction.ts) so the preview
 *    keeps the legacy's hover tooltip + drag-rotate (canvas.view3d.js is the
 *    SAME View class for the board and this mini preview — hover-tooltip and
 *    OrbitControls drag-rotate came for free there; the bare Canvas3DGeometry
 *    this hook used to build had no picking layer at all, so neither did
 *    anything here — see the doc comment on attachHoverInteraction);
 *  - disposes the preview on unmount (the legacy leaks it — documented dedrift).
 *
 * The live canvas3d is read off the module-level singleton registry
 * (getLiveCanvas3D() — three/canvas3dRegistry.ts; was window.__c3d before the
 * pre-INTEGRATE cleanup). NB the factory differs in shape from the legacy fn map
 * (which returned a Backbone canvasView wrapping .canvas3d): here it returns the
 * Canvas3DInteraction instance directly, so this hook drives that instance.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createAccessoryPreviewCanvas } from '../../three/canvas3dInteraction';
import { getLiveCanvas3D } from '../../three/canvas3dRegistry';

interface PreviewArgs {
  accessoryType: 'damper' | 'louver' | 'hood' | 'opening';
  accessoryId: number;
  /** cms_accessory.mounting_plane rows (plane resolution for previewMounting). */
  cms: Record<string, unknown>;
  /** the live section row (drawAccessory needs section dims). */
  section: Record<string, unknown> & { id: number };
}

/**
 * COMBO LOUVER — A2.1. The combo working state the SELECTOR owns
 * (`_comboMode` / `_comboFaces` / `_comboLouverCounts`), which
 * `_updateCanvasPreview` (:3415-3428, :3446) folds into the preview payload.
 * Absent (or `comboFaces: null`) → the plain-louver path, unchanged.
 */
export interface ComboPreviewExtras {
  /** `self._comboMode && self._comboFaces` — the injection gate (:3416). */
  comboFaces: Array<Record<string, unknown>> | null;
  /**
   * `checkForComboLouverAmbiguousMatch` (:3446) — the shared identity still
   * matches more than one catalog product line, the same ambiguity that forces
   * auto-size off. No single product to draw against, so the box is pulled OFF
   * the canvas until the identity narrows to one. (Q14: this count is
   * `matches / total` over `combo_smartlist`.)
   */
  ambiguousMatch: boolean;
}

interface PreviewApi {
  /** attach (or re-attach) the preview renderer into `host` (AHU tab visible). */
  attach: (host: HTMLElement | null) => void;
  /** write staging data + (re)draw / remove the staged accessory. */
  updatePreview: (
    stagingModel: Record<string, unknown>,
    stagingPosition: Record<string, unknown>,
    combo?: ComboPreviewExtras
  ) => void;
  /** #btn_view_reset → canvas:set:view('default') on the PREVIEW canvas. */
  resetView: () => void;
  /** dispose (cancel/select teardown). */
  dispose: () => void;
}

const getLiveCanvas = (): any => getLiveCanvas3D() ?? null;

export function useAccessoryPreviewCanvas({
  accessoryType,
  accessoryId,
  cms,
  section,
}: PreviewArgs): PreviewApi {
  const previewRef = useRef<any>(null);
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const initializingRef = useRef(false);
  const lastBladeRef = useRef<unknown>(undefined);
  const detachHoverRef = useRef<(() => void) | null>(null);

  const dispose = useCallback(() => {
    detachHoverRef.current?.();
    detachHoverRef.current = null;
    if (previewRef.current) {
      try {
        previewRef.current.dispose?.();
      } catch {
        /* legacy leaks the preview; dispose best-effort */
      }
      previewRef.current = null;
    }
    canvasElRef.current = null;
    initializingRef.current = false;
  }, []);

  useEffect(() => dispose, [dispose]);

  // _updateCanvasPreview (:1660-1738).
  const updatePreview = useCallback(
    (
      stagingModel: Record<string, unknown>,
      stagingPosition: Record<string, unknown>,
      combo?: ComboPreviewExtras
    ) => {
      const preview = previewRef.current;
      if (!preview) return;
      const pos = { ...stagingPosition };
      const model = { ...stagingModel };
      const staged = { ...stagingModel };
      delete (staged as { name?: unknown }).name;
      preview.previewAccessoryStagingData = staged;

      const planeId = pos.mounting_plane_id;
      const planeList = (cms.mounting_plane as Array<{ id: number }> | undefined) ?? [];
      const plane = planeId != null ? planeList.find((p) => String(p.id) === String(planeId)) : null;
      const label = accessoryType.toUpperCase();
      const acc: any = { ...model, id: accessoryId, name: label };
      // COMBO LOUVER — A2.1 (:3415-3428): the enclosure box + face louvers are
      // driven by is_combo + combo_faces, and the staging model's own
      // combo_faces can be stale/empty while editing, so hand the canvas the
      // CURRENT per-face working state (KA-5249).
      if (combo?.comboFaces) {
        acc.is_combo = true;
        acc.combo_faces = combo.comboFaces;
      }
      const width = acc.width || acc.overall_width || acc.width_calc;
      const height = acc.height || acc.overall_depth || acc.height_calc;
      const masterField = `master_${accessoryType}_id`;

      // removal rules (:1679-1694).
      const bladeDirections = (model as any).blade_directions;
      // COMBO LOUVER — A2.1 (:3435-3442): a combo has no top-level
      // master_louver_id (it uses per-face masters) and is always drawable, so
      // never treat it as "remove". Note the gate reads the STAGING MODEL's
      // is_combo, not the injected flag.
      const checkForLouverAndDamper =
        !model.is_combo &&
        (accessoryType === 'damper' || accessoryType === 'louver') &&
        (String(planeId) === '9' ||
          !model[masterField] ||
          !!(
            (lastBladeRef.current as any) &&
            planeId &&
            bladeDirections &&
            !bladeDirections[planeId as any]?.find(
              (el: any) => String(el.id) === String(model.blade_direction_id)
            )
          ));
      const checkForHoodStyle4NoHeight2 =
        accessoryType === 'hood' && String(model.style_id) === '4' && model.height_2 == null;
      const shouldRemove =
        checkForLouverAndDamper || checkForHoodStyle4NoHeight2 || !!combo?.ambiguousMatch;
      if (shouldRemove) {
        if (preview.accessoryCanvasObjectsObj[accessoryId]) {
          preview.destroyAccessory(accessoryId);
          preview.need_update = true;
        }
        lastBladeRef.current = model.blade_direction_id;
        return;
      }

      const depth =
        acc.depth || (accessoryType === 'hood' && acc.depth_mode_id === 1 ? height : null);
      const entry = preview.accessoryCanvasObjects.find(
        (obj: any) => obj.accessory.id === accessoryId
      );
      // The geometry draw needs a resolved plane + position vector; an
      // incomplete staging (no plane / no x,y yet) would crash
      // drawAccessoriesCubeComplete on `.x`. Guard on the plane like the
      // mounting branch does, and tolerate any residual draw error (the live
      // canvas itself swallows partial-accessory draw failures).
      const drawSafe = (drawArgs: any) => {
        try {
          preview.drawAccessory(drawArgs);
        } catch {
          /* partial staging — skip this draw frame (legacy live canvas tolerates) */
        }
      };
      if (entry) {
        entry.accessory = acc;
        entry.mounting = pos;
        const posNonNull = Object.fromEntries(
          Object.entries(pos).filter(([, v]) => v != null)
        );
        preview.previewMounting = {
          ...entry.mounting,
          ...posNonNull,
          ...(plane ? { mounting_plane: plane } : {}),
        };
        if (pos.mounting_plane_id && width && height) {
          drawSafe({
            accessory: acc,
            section: entry.section,
            width,
            height,
            depth,
            name: label,
            mounting: preview.previewMounting,
            isAlredyRendered: !!preview.accessoryCanvasObjectsObj[accessoryId],
          });
        }
      } else {
        const mounting: Record<string, any> = { ...pos, ...(plane ? { mounting_plane: plane } : {}) };
        // ★ `drawAccessory` wants a canvas SECTION MODEL, not the plain section
        // row. Legacy hands it `self.section`, a Backbone model, and every draw
        // path below dereferences it as one — `section.tunnel.get('airflow_
        // direction_id')` (canvas3dGeometry :5148/:5162) and, for the hood,
        // `sectionCanvasObjects.find(el => el.section.get('id') === section.get('id'))`
        // (:6692). The selector's `section` prop is the activate payload's plain
        // row, so `.get` is undefined and the draw throws — which `drawSafe`
        // then swallows, so the accessory silently never appears.
        //
        // The `entry` branch above never hit this because it reuses
        // `entry.section`, already a canvas model. This branch is the
        // NEVER-DRAWN accessory — e.g. a hood whose W/H/H2 are still empty when
        // the dialog opens, which is exactly the QA case: typing the Selected
        // Airway dimensions produced no hood on the AHU canvas.
        const sectionModel = preview.resolveSectionModel(section) ?? section;
        if (mounting.mounting_plane_id && width && height) {
          preview.previewMounting = mounting;
          drawSafe({
            accessory: acc,
            section: sectionModel,
            width,
            height,
            depth,
            name: label,
            mounting,
            isAlredyRendered: false,
          });
        }
      }
      preview.need_update = true;
      lastBladeRef.current = model.blade_direction_id;
    },
    [accessoryType, accessoryId, cms, section]
  );

  // _attachAhuCanvas (:1740-1780).
  const attach = useCallback(
    (host: HTMLElement | null) => {
      if (!host) return;
      const live = getLiveCanvas();
      if (!live) return;
      if (previewRef.current) {
        const preview = previewRef.current;
        host.appendChild(preview.renderer.domElement);
        // re-point the el so getAhuSizes/redrawCanvas read the live container
        // (legacy upside-down fix).
        preview.el = host;
        requestAnimationFrame(() => {
          if (!previewRef.current) return;
          const w = host.clientWidth;
          const h = host.clientHeight;
          if (w > 0 && h > 0) {
            preview.renderer.setSize(w, h);
            preview.need_update = true;
          }
        });
        return;
      }
      if (initializingRef.current) return;
      initializingRef.current = true;
      const canvas = document.createElement('canvas');
      canvasElRef.current = canvas;
      try {
        const preview = createAccessoryPreviewCanvas(live, {
          el: host,
          canvas,
          accessoryId,
        });
        previewRef.current = preview;
        initializingRef.current = false;
        host.appendChild(preview.renderer.domElement);
        detachHoverRef.current = preview.attachHoverInteraction();
      } catch (e) {
        initializingRef.current = false;
        // eslint-disable-next-line no-console
        console.error('[accessory preview canvas] init failed', e);
      }
    },
    [accessoryId]
  );

  // resetToDefaultView — set the PREVIEW view to 'default' (legacy fires
  // canvas:set:view which the live canvas consumes; here we target the preview
  // instance directly so the mini-canvas resets, not the rotated board canvas).
  const resetView = useCallback(() => {
    previewRef.current?.setView?.('default');
  }, []);

  return { attach, updatePreview, resetView, dispose };
}
